import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Printer, ShoppingBag, Truck } from "lucide-react";
import StatusLabel from "../../../components/ui/StatusLabel";
import Button from "../../../components/ui/Button";
import WizardShell from "../../../components/ui/WizardShell";
import BottomBar from "../../../components/ui/BottomBar";
import EmptyState from "../../../components/ui/EmptyState";
import { dismissMessageBar, showMessageBar } from "../../../util/messageBar";
import { setActiveBookId, setFetchingImages } from "../../../redux/slice/stepSlice";
import { fetchBatchImagesByBookId } from "../../../services/mediaService";
import { useDispatch, useSelector } from 'react-redux';
import { resetStep } from '../../../redux/slice/stepSlice';
import { setCoverImages, setImages } from '../../../redux/slice/sketchSlice';
import { selectBooksLoadedAt } from '../../../redux/slice/profileSlice';
import { useNavigate } from 'react-router-dom';
import { redirectToBridgePage, fetchProduct } from '../../../services/shopifyService';
import { recordShopifyCheckoutStalled } from '../../../services/bookService';
import { toast } from 'react-toastify';
import { setTitle } from '../../../helpers/MetaTag';
import { updateShopifyCartQuantity, removeShopifyCartItem, clearShopifyCart, pruneSelectedPlanLocalStorage, syncLocalCartToDB } from '../../../services/shopifyCartService';
import { calculateTieredPricing, isBulkDiscounted, priceOrderBooks, ADDITIONAL_BOOK_PRICE, BASE_BOOK_PRICE, PDF_ADDON_PRICE, PDF_DOWNLOAD_PRICE } from '../../../config/pricing';
import { PRINTABLE_PDF_ADDON_PRODUCT_ID, DOWNLOAD_PDF_PRODUCT_ID } from '../../../util/pdfCheckout';
import { isPdfBook, switchBookFormat } from '../../../util/bookFormat';
import CoverPreview from "../../../components/builder/CoverPreview";
import { PdfOfferCard, PdfRow } from "../../../components/cart/PdfOffer";

// After sending the browser to Shopify, how long before Checkout counts as stuck.
const CHECKOUT_STALL_MS = 4000;

function ShopifyCart() {
  const [cartItems, setCartItems] = useState([]);
  // The latest cart for Undo callbacks that run after later renders.
  const cartRef = useRef(cartItems);
  cartRef.current = cartItems;
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);

  // Checkout leaves for Shopify in this tab, so isProcessingOrder is never cleared on success. Back from
  // Shopify can restore this page from the back-forward cache with that state intact, leaving the
  // Checkout button spinning and the order locked. A cache restore means checkout was abandoned.
  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) setIsProcessingOrder(false);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  // Checkout has sent the browser to Shopify. If this page is still here a few seconds later the
  // browser didn't go (Chrome on iPhone, 2026-11-02), so give Checkout back instead of spinning
  // forever. Leaving the page (pagehide) cancels it.
  const checkoutStallTimer = useRef(null);
  useEffect(() => {
    const cancel = () => clearTimeout(checkoutStallTimer.current);
    window.addEventListener("pagehide", cancel);
    return () => {
      cancel();
      window.removeEventListener("pagehide", cancel);
    };
  }, []);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const myBooks = useSelector(state => state.profile.myBooks);
  const booksLoadedAt = useSelector(selectBooksLoadedAt);
  const profileData = useSelector(state => state.profile.data);
  // const activeBookId = useSelector(state => state.stepFlow.activeBookId);


  const getBook = id => myBooks.find(book => book._id === id);

  const getCartItemDisplayTitle = (item) => {
    const book = getBook(item.bookId);
    return book?.title || item.title || item.name || "this book";
  };

  // A line added moments ago may not have its server `_id` yet (its POST /add is still in flight),
  // so there is no row id to update. Reconcile the whole cart by book + variant instead of skipping
  // the server. An empty cart is never pushed by the sync, so clear it explicitly.
  const reconcileServerCart = (remainingItems) => {
    if (remainingItems.length) {
      syncLocalCartToDB();
    } else {
      clearShopifyCart();
    }
  };

  /**
   * Take a book out of the order with an Undo (docs/design/HANDOFF.md: "remove
   * with Undo"). The order updates at once; the server only hears about it when
   * the message bar closes without Undo, so putting it back costs nothing.
   */
  const removeWithUndo = (item) => {
    if (isProcessingOrder) return;
    const title = getCartItemDisplayTitle(item);
    const snapshot = cartItems;
    const remaining = cartItems.filter((i) => i.bookId !== item.bookId);
    setCartItems(remaining);
    syncCartToLocalStorage(remaining);
    const commit = () => {
      if (item._id) {
        removeShopifyCartItem(item._id).catch((err) => console.error("Async cart remove failed:", err));
      } else {
        reconcileServerCart(remaining);
      }
    };
    pendingRemovals.current.set(item.bookId, commit);
    showMessageBar({
      text: `${title} removed from your order`,
      onUndo: () => {
        pendingRemovals.current.delete(item.bookId);
        setCartItems(snapshot);
        syncCartToLocalStorage(snapshot);
      },
      onClose: ({ undone }) => {
        if (undone) return;
        if (pendingRemovals.current.delete(item.bookId)) commit();
      },
    });
  };

  // Removals waiting on the Undo bar still reach the server if the page is left or closed first.
  const pendingRemovals = useRef(new Map());
  useEffect(() => {
    const flush = () => {
      pendingRemovals.current.forEach((commit) => commit());
      pendingRemovals.current.clear();
    };
    window.addEventListener("pagehide", flush);
    return () => {
      flush();
      // Undo can't put a book back on a page that's gone.
      dismissMessageBar();
      window.removeEventListener("pagehide", flush);
    };
  }, []);

  const handleDecreaseQuantity = (item, currentQty) => {
    if (currentQty <= 1) {
      removeWithUndo(item);
      return;
    }
    handleChangeQuantity(item.bookId, -1);
  };

  setTitle("Memory Doodle | Your order");

  const getItemQuantity = (item) => {
    if (!item) return 0;
    const qty = Number(item?.quantity);
    if (!Number.isNaN(qty) && qty > 0) return qty;
    return 1;
  };

  const loadCartFromLocalStorage = () => {
    try {
      // Only prune on a book list this document can vouch for: another tab's write lands here
      // too, and an untrusted empty list would delete the cart for every tab.
      if (booksLoadedAt) {
        setCartItems(pruneSelectedPlanLocalStorage(myBooks, booksLoadedAt));
        return;
      }

      const raw = localStorage.getItem("selectedPlan");
      if (!raw) {
        setCartItems([]);
        return;
      }

      let parsed = JSON.parse(raw);

      // Normalize object -> array
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        parsed = [parsed];
        localStorage.setItem("selectedPlan", JSON.stringify(parsed));
      }

      if (!Array.isArray(parsed)) {
        setCartItems([]);
        return;
      }

      // Without a trusted book list, only hide unknown book IDs; storage is left as is.
      const validItems = parsed.filter(item => myBooks.find(book => book._id === item.bookId));
      setCartItems(validItems);
    } catch (error) {
      console.error("Error reading selectedPlan from localStorage in ShopifyCart:", error);
      setCartItems([]);
    }
  };

  const syncCartToLocalStorage = (items) => {
    try {
      if (!items || !items.length) {
        localStorage.removeItem("selectedPlan");
      } else {
        localStorage.setItem("selectedPlan", JSON.stringify(items));
      }
      window.dispatchEvent(new Event("cartUpdated"));
    } catch (error) {
      console.error("Error syncing selectedPlan to localStorage in ShopifyCart:", error);
    }
  };

  useEffect(() => {
    loadCartFromLocalStorage();

    const handleStorage = (event) => {
      if (event.key === "selectedPlan" || event.key === null) {
        loadCartFromLocalStorage();
      }
    };

    const handleCartUpdated = () => {
      loadCartFromLocalStorage();
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener("cartUpdated", handleCartUpdated);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("cartUpdated", handleCartUpdated);
    };
  }, [myBooks, booksLoadedAt]);

  const handleAddBook = () => {
    dispatch(resetStep());
    dispatch(setCoverImages([]));
    dispatch(setImages([]));
    navigate("/create-book?stepCode=Create_New_Book");
  };

  // Handle quantity change per book (by bookId)
  const handleChangeQuantity = (bookId, delta) => {
    setCartItems((prevItems) => {
      if (!prevItems || !prevItems.length) return prevItems || [];

      const items = prevItems.map((item) => ({ ...item }));
      const index = items.findIndex((item) => item.bookId === bookId);

      if (index === -1) return items;

      const currentItem = items[index];
      const currentQty = getItemQuantity(currentItem);
      const newQty = currentQty + delta;

      // If quantity goes to 0 or below, remove this book from cart
      if (newQty <= 0) {
        const updatedItems = items.filter((item) => item.bookId !== bookId);
        syncCartToLocalStorage(updatedItems);

        // Async API Call
        if (currentItem._id) {
          removeShopifyCartItem(currentItem._id).catch(err => console.error("Async cart remove failed:", err));
        } else {
          reconcileServerCart(updatedItems);
        }

        return updatedItems;
      }

      // Update quantity
      const updated = {
        ...currentItem,
        quantity: newQty,
        planPricingData: {
          ...currentItem.planPricingData,
          quantity: newQty,
        },
      };

      items[index] = updated;
      syncCartToLocalStorage(items);

      // Async API Call
      if (currentItem._id) {
        updateShopifyCartQuantity({ id: currentItem._id, quantity: newQty }).catch(err => console.error("Async cart update failed:", err));
      } else {
        reconcileServerCart(items);
      }

      return items;
    });
  };

  // PDF-only books (decided 2026-10-26): $9.95 each, priced on their own. They never count toward the
  // printed pricing ($24.95 first copy, $10 each after), have no copies and no $5 add-on.
  const isPdfItem = (item) => isPdfBook(getBook(item.bookId));
  const printedItems = cartItems.filter((item) => !isPdfItem(item));
  const pdfOnlyBookCount = new Set(cartItems.filter((i) => i.bookId && isPdfItem(i)).map((i) => i.bookId)).size;

  // Printed copies across all printed books.
  const totalQuantity = printedItems.reduce(
    (sum, item) => sum + getItemQuantity(item),
    0
  );

  // One Printable PDF Add-On per unique printed book: copies of a book share one PDF.
  // The choice is `includePdf` on the book's cart line, so removing the book
  // removes its PDF with it.
  const hasPdf = (bookId) => printedItems.some((i) => i.bookId === bookId && i.includePdf);
  const pdfCount = new Set(
    printedItems.filter((i) => i.bookId && i.includePdf).map((i) => i.bookId)
  ).size;

  // Printed books use tiered pricing for the whole order. Each book shows its own share (decided
  // 2026-10-26, "Show savings when prices drop"): the first book carries the $24.95 copy, the rest
  // are $10 a copy, 55% off list at 10+; a discounted book shows its full price struck through and why.
  const bookSubtotal = calculateTieredPricing(totalQuantity);
  const orderPricing = priceOrderBooks(printedItems.map((item) => getItemQuantity(item)));
  const pricingOf = (bookId) => orderPricing.books[printedItems.findIndex((i) => i.bookId === bookId)] || null;
  const pdfOnlySubtotal = pdfOnlyBookCount * PDF_DOWNLOAD_PRICE;
  const subtotal = bookSubtotal + pdfCount * PDF_ADDON_PRICE + pdfOnlySubtotal;


  // Written outside a state updater: the cartUpdated event re-renders the header,
  // which React rejects from inside another component's render.
  // Turning a PDF-only book into a printed one keeps the PDF they chose as the $5 add-on, ticked
  // (OrderFormats: "Printed book added, PDF kept"); switching back clears it. Both have Undo.
  const [formatBusy, setFormatBusy] = useState(null);
  const setLine = (bookId, patch) => {
    const next = cartRef.current.map((i) => (i.bookId === bookId ? { ...i, ...patch } : i));
    setCartItems(next);
    syncCartToLocalStorage(next);
  };
  const toPrinted = async (bookId, { undoable = true } = {}) => {
    if (formatBusy) return;
    setFormatBusy(bookId);
    const res = await switchBookFormat(dispatch, bookId, "print");
    setFormatBusy(null);
    if (!res?.success) {
      toast.error(res?.message || "We couldn't change that. Please try again.");
      return;
    }
    setLine(bookId, { includePdf: true, startedAsPdf: true });
    if (undoable) showMessageBar({ text: "Printed book added, PDF kept", onUndo: () => toPdfOnly(bookId, { undoable: false }) });
  };
  const toPdfOnly = async (bookId, { undoable = true } = {}) => {
    if (formatBusy) return;
    setFormatBusy(bookId);
    const res = await switchBookFormat(dispatch, bookId, "pdf");
    setFormatBusy(null);
    if (!res?.success) {
      toast.error(res?.message || "We couldn't change that. Please try again.");
      return;
    }
    setLine(bookId, { includePdf: false });
    if (undoable) showMessageBar({ text: "Switched back to PDF only", onUndo: () => toPrinted(bookId, { undoable: false }) });
  };

  const setPdfIncluded = (bookIds, include) => {
    if (!cartItems.length) return;
    const updatedItems = cartItems.map((item) =>
      bookIds.includes(item.bookId) ? { ...item, includePdf: include } : item
    );
    setCartItems(updatedItems);
    syncCartToLocalStorage(updatedItems);
  };

  // Handle checkout process
  const handleCheckout = async () => {
    const tappedAt = Date.now();
    const preSelectedPlan = localStorage.getItem("selectedPlan");

    if (!preSelectedPlan) {
      toast.error("No plan selected. Please add books to your order.");
      navigate("/choose-plan");
      return;
    }

    if (!cartItems || cartItems.length === 0) {
      toast.error("Your order is empty. Add a book from your collection or create one first.");
      navigate("/choose-plan");
      return;
    }

    setIsProcessingOrder(true);

    let parsed;
    try {
      parsed = JSON.parse(preSelectedPlan);
    } catch (err) {
      console.error(" Failed to parse selectedPlan from localStorage:", err);
      toast.error("Your selected plan data looks corrupted. Please choose a plan again.");
      setIsProcessingOrder(false);
      navigate("/choose-plan");
      return;
    }

    // Ensure it's an array
    let cartArray = Array.isArray(parsed) ? parsed : [parsed];

    if (cartArray.length === 0) {
      toast.error("Your order is empty. Add a book from your collection or create one first.");
      setIsProcessingOrder(false);
      navigate("/choose-plan");
      return;
    }

    try {
      const shopifyCustomerId = localStorage.getItem("shopifyCustomerId") || "";

      console.log(` Preparing checkout for ${cartArray.length} books:`, cartArray);

      // Build arrays for all books
      const bookIds = [];
      const quantities = [];
      const titles = [];
      const variantIds = [];
      const versions = [];
      const recipients = [];
      const previewUrls = [];

      // A PDF-only book checks out as the $9.95 PDF product, quantity 1, with its _bookId; the order
      // webhook knows that product is never printed (utils/helpers/shopifyDigitalLine.js in the API).
      let pdfOnlyVariantId = null;
      if (cartArray.some((i) => isPdfBook(myBooks.find(({ _id }) => i.bookId === _id)))) {
        const pdfOnlyProduct = await fetchProduct(DOWNLOAD_PDF_PRODUCT_ID);
        pdfOnlyVariantId = pdfOnlyProduct?.variants?.edges?.[0]?.node?.id || null;
        if (!pdfOnlyVariantId) {
          throw new Error("Could not load the printable PDF");
        }
      }

      // Process each book in cart
      for (const item of cartArray) {
        const bookData = myBooks.find(({ _id }) => item.bookId === _id);

        if (!bookData) {
          console.warn(` Book data not found for bookId: ${item.bookId}, skipping`);
          continue;
        }

        const pdfOnly = isPdfBook(bookData);
        const variantId = pdfOnly ? pdfOnlyVariantId : (
          item.variantId ||
          item.shopifyVariantId ||
          (typeof item.id === "string" &&
            item.id.includes("gid://shopify/ProductVariant/")
            ? item.id
            : null));

        if (!variantId) {
          console.warn(` Variant ID missing for book ${item.bookId}, skipping`);
          continue;
        }

        const quantity = pdfOnly ? 1 : getItemQuantity(item);

        // Add to arrays
        bookIds.push(item.bookId);
        quantities.push(quantity.toString());
        titles.push(bookData.title || item.title || item.name || "Custom Coloring Book");
        variantIds.push(variantId);
        versions.push((bookData.version || item.version || 1).toString());
        recipients.push(bookData.recipientName || item.recipient || "");
        previewUrls.push(bookData.previewUrl || bookData.coverImage || item.previewUrl || item.image || "");
      }

      // Validate we have at least one valid book
      if (bookIds.length === 0) {
        throw new Error("No valid books found in your order. Please try adding items again.");
      }

      // Printable PDF Add-On: one extra line per unique book that has it, with the
      // same _bookId so the webhook knows which book's PDF to deliver. It goes in
      // the same arrays as the books; the webhook tells it apart by product.
      const bookLineCount = bookIds.length;
      const pdfBookIndexes = bookIds
        .map((bookId, index) => index)
        .filter((index) =>
          bookIds.indexOf(bookIds[index]) === index && // first line of each book only
          cartArray.some((i) => i.bookId === bookIds[index] && i.includePdf) &&
          !isPdfBook(myBooks.find(({ _id }) => _id === bookIds[index])) // a PDF-only book is already the PDF
        );

      let pdfVariantId = null;
      if (pdfBookIndexes.length) {
        const pdfProduct = await fetchProduct(PRINTABLE_PDF_ADDON_PRODUCT_ID);
        pdfVariantId = pdfProduct?.variants?.edges?.[0]?.node?.id || null;
        if (!pdfVariantId) {
          throw new Error("Could not load the printable PDF add-on");
        }
        pdfBookIndexes.forEach((index) => {
          bookIds.push(bookIds[index]);
          quantities.push("1");
          titles.push(titles[index]);
          variantIds.push(pdfVariantId);
          versions.push(versions[index]);
          recipients.push(recipients[index]);
          previewUrls.push(previewUrls[index]);
        });
      }

      console.log(" Multi-book checkout data prepared:", {
        bookCount: bookIds.length,
        bookIds,
        quantities,
        variantIds
      });

      // Build checkout data with arrays
      const checkoutData = {
        // Multi-book data (comma-separated for simple params, ||| for text with commas)
        bookIds: bookIds.join(','),
        quantities: quantities.join(','),
        titles: titles.join('|||'), // Use ||| to avoid issues with commas in titles
        variantIds: variantIds.join(','),
        versions: versions.join(','),
        recipients: recipients.join('|||'),
        previewUrls: previewUrls.join('|||'),

        // Shared data
        customerId: shopifyCustomerId,
        email: profileData?.email || '',

        // Legacy fields (for backward compatibility if needed)
        bookId: bookIds[0], // First book ID
        variantId: variantIds[0], // First variant ID
        quantity: quantities.slice(0, bookLineCount).reduce((sum, q) => sum + parseInt(q), 0).toString(), // Total book quantity
        title: titles[0], // First book title
        version: versions[0],
        recipient: recipients[0],
        previewUrl: previewUrls[0],
      };

      console.log(" Final checkout data:", checkoutData);

      // --- GTM: Push "add_to_cart" event to dataLayer ---
      const productId = "gid://shopify/Product/9314844639480";

      const gtmItems = cartArray.map((item, idx) => {
        // A PDF-only book is the $9.95 Digital Coloring Book product (the old direct PDF checkout reported it the same way).
        if (isPdfBook(myBooks.find(({ _id }) => _id === item.bookId))) {
          const pdfVariant = variantIds[bookIds.indexOf(item.bookId)] || "";
          return {
            item_name: "Digital Coloring Book",
            item_id: pdfVariant,
            product_id: DOWNLOAD_PDF_PRODUCT_ID,
            book_id: item.bookId || "",
            variant_id: pdfVariant,
            price: PDF_DOWNLOAD_PRICE,
            quantity: 1,
          };
        }
        const qty = getItemQuantity(item);
        // The per-copy price of this book's share, as Review order shows it (the total is unchanged).
        const bookPrice = pricingOf(item.bookId)?.price ?? 0;
        const itemName = item.shopifyProductId !== productId
          ? "Connection Kit"
          : "Personalized Coloring Book";

        return {
          item_name: itemName,
          item_id: variantIds[idx] || item.variantId || item.shopifyVariantId || "",
          product_id: item.shopifyProductId || "",
          book_id: item.bookId || "",
          variant_id: variantIds[idx] || item.variantId || item.shopifyVariantId || "",
          price: parseFloat((qty ? bookPrice / qty : 0).toFixed(2)),
          quantity: qty,
        };
      });

      pdfBookIndexes.forEach((index) => {
        gtmItems.push({
          item_name: "Printable PDF Add-On",
          item_id: pdfVariantId,
          product_id: PRINTABLE_PDF_ADDON_PRODUCT_ID,
          book_id: bookIds[index],
          variant_id: pdfVariantId,
          price: PDF_ADDON_PRICE,
          quantity: 1,
        });
      });

      const gtmTotalQuantity = gtmItems.reduce((sum, i) => sum + i.quantity, 0);

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "add_to_cart",
        total_quantity: gtmTotalQuantity,
        ecommerce: {
          value: parseFloat(subtotal.toFixed(2)),
          items: gtmItems,
        },
      });

      console.log("[GTM] Sending add_to_cart event to Tag Manager:", {
        total_quantity: gtmTotalQuantity,
        ecommerce_value: parseFloat(subtotal.toFixed(2)),
        items: gtmItems,
      });
      // --- End GTM ---

      // Redirect to bridge page (same tab)
      await redirectToBridgePage(checkoutData);

      // TODO(design): no designed state for checkout not opening; uses the message bar (docs/design/GAPS.md).
      clearTimeout(checkoutStallTimer.current);
      checkoutStallTimer.current = setTimeout(() => {
        setIsProcessingOrder(false);
        showMessageBar({ text: "Still opening checkout. If nothing happens, tap Checkout again.", duration: 8000 });
        recordShopifyCheckoutStalled({
          bookIds: [...new Set(bookIds)],
          waitedMs: Date.now() - tappedAt,
        }).catch(() => {});
      }, CHECKOUT_STALL_MS);

    } catch (error) {
      console.error(" Checkout preparation failed:", error);
      toast.error(`Something went wrong: ${error.message}. Please try again.`);
      setIsProcessingOrder(false);
    }
  };

  /* if (!activeBookId) {
    navigate('/dashboard');
    return <></>
  } */

  // ---- Review order on the redesign (docs/design/HANDOFF.md: Order, OrderCart and their tablet and desktop versions)

  const money = (n) => `$${Number(n || 0).toFixed(2)}`;
  const uniqueBooks = cartItems.filter((item, index) => !!item.bookId && cartItems.findIndex((i) => i.bookId === item.bookId) === index);
  const printedBooks = uniqueBooks.filter((item) => !isPdfItem(item));
  const anyPdfOnly = pdfOnlyBookCount > 0;
  // The one-printed-book layout (Order, Tab6Order, Desk6Order); a PDF-only book always uses the list layout.
  const single = uniqueBooks.length === 1 && printedBooks.length === 1;

  /** Reopen a book on its Preview step. */
  const editBook = (bookId) => {
    if (isProcessingOrder) return;
    dispatch(setActiveBookId({ bookId }));
    // Preview reads the book's photos from redux (same load as opening a book from My Books).
    dispatch(setFetchingImages(true));
    fetchBatchImagesByBookId(bookId)
      .then((res) => {
        const docs = res?.docs?.data || [];
        if (docs.length) {
          dispatch(setCoverImages(docs.filter((img) => !!img.isCover)));
          dispatch(setImages(docs.filter((img) => !img.isCover)));
        }
      })
      .finally(() => dispatch(setFetchingImages(false)));
    navigate("/review?stepCode=Review_Your_Book");
  };

  const countText = anyPdfOnly
    ? `${uniqueBooks.length} ${uniqueBooks.length === 1 ? "book" : "books"}`
    : `${uniqueBooks.length} ${uniqueBooks.length === 1 ? "book" : "books"} · ${totalQuantity} ${totalQuantity === 1 ? "copy" : "copies"}`;

  const Stepper = ({ item, qty }) => (
    <div className="flex items-center overflow-hidden rounded-xl border border-md-control bg-white">
      <button
        type="button"
        aria-label={`Remove a copy of ${getCartItemDisplayTitle(item)}`}
        onClick={() => handleDecreaseQuantity(item, qty)}
        disabled={isProcessingOrder}
        className="h-11 w-11 text-[22px] font-medium leading-none text-md-dark disabled:opacity-40"
      >
        −
      </button>
      <span className="w-9 text-center text-[17px] font-bold text-md-dark" aria-live="polite">
        {qty}
      </span>
      <button
        type="button"
        aria-label={`Add a copy of ${getCartItemDisplayTitle(item)}`}
        onClick={() => handleChangeQuantity(item.bookId, 1)}
        disabled={isProcessingOrder}
        className="h-11 w-11 text-[22px] font-medium leading-none text-md-dark disabled:opacity-40"
      >
        +
      </button>
    </div>
  );

  // The Printable PDF offer (HANDOFF, "Printable PDF offer, redesigned"): one book, a "Start coloring today"
  // card (inside the book's card from tablet up, below it on phones); several, a tick row per book and one
  // card whose button adds or removes the PDF for every book.
  // The add-on offer counts printed books only; with none it isn't shown (OrderFormats, OrderPdfOnly).
  const bookIdsInOrder = printedBooks.map((i) => i.bookId);
  const pdfAddedCount = bookIdsInOrder.filter((id) => hasPdf(id)).length;
  const toggleOfferPdf = () => {
    const allOn = bookIdsInOrder.length > 0 && pdfAddedCount === bookIdsInOrder.length;
    setPdfIncluded(bookIdsInOrder, !allOn);
  };
  const offerCard = (props) => (
    <PdfOfferCard
      count={bookIdsInOrder.length}
      added={pdfAddedCount}
      onToggle={toggleOfferPdf}
      addOnPrice={PDF_ADDON_PRICE}
      fullPrice={PDF_DOWNLOAD_PRICE}
      disabled={isProcessingOrder}
      lead={bookIdsInOrder.length === 1 && anyPdfOnly ? "Get your printed book as a printable PDF too" : undefined}
      {...props}
    />
  );

  const coverOf = (book, item) => book?.coverImageThumbnail || item?.coverImageThumbnail || book?.coverImage || item?.coverImage || item?.image || null;

  const totals = (
    <div className="flex flex-col gap-1.5 px-1 text-[15px]">
      {/* With a PDF-only book the lines name each kind (OrderFormats): "Printed books", "PDF-only books", "PDF add-ons", each only when non-zero. */}
      {totalQuantity > 0 ? (
        <div className="flex justify-between">
          <span className="text-md-muted">
            {anyPdfOnly
              ? `Printed books (${totalQuantity} ${totalQuantity === 1 ? "copy" : "copies"})`
              : single
                ? `Book${totalQuantity > 1 ? ` (${totalQuantity} copies)` : ""}`
                : `Books (${totalQuantity} ${totalQuantity === 1 ? "copy" : "copies"})`}
          </span>
          <span className="text-md-dark">{money(orderPricing.list)}</span>
        </div>
      ) : null}
      {/* The savings that bring the books from full price to what they cost (always adds up to the subtotal). */}
      {orderPricing.savings > 0 ? (
        <div className="flex justify-between font-semibold text-green-800">
          <span>{orderPricing.savingsLabel}</span>
          <span>−{money(orderPricing.savings)}</span>
        </div>
      ) : null}
      {anyPdfOnly ? (
        <div className="flex justify-between">
          <span className="text-md-muted">PDF-only books ({pdfOnlyBookCount})</span>
          <span className="text-md-dark">{money(pdfOnlySubtotal)}</span>
        </div>
      ) : null}
      {pdfCount > 0 ? (
        <div className="flex justify-between">
          <span className="text-md-muted">{anyPdfOnly ? `PDF add-ons (${pdfCount})` : `Printable PDF${pdfCount > 1 ? ` (${pdfCount})` : ""}`}</span>
          <span className="text-md-dark">{money(pdfCount * PDF_ADDON_PRICE)}</span>
        </div>
      ) : null}
      <div className="flex justify-between gap-4">
        <span className="text-md-muted">Shipping</span>
        {/* Only PDFs: nothing ships. */}
        <span className="text-right text-md-muted">{totalQuantity > 0 ? "Calculated at checkout" : "None. Emailed right away."}</span>
      </div>
      <div className="mt-1 flex justify-between border-t border-md-pink-light-3 pt-2.5 text-[17px] font-bold text-md-dark lg:text-[19px]">
        <span>Subtotal</span>
        <span>{money(subtotal)}</span>
      </div>
    </div>
  );

  const checkoutButton = (label) => (
    <Button size="lg" fullWidth onClick={handleCheckout} busy={isProcessingOrder} disabled={!cartItems.length}>
      {isProcessingOrder ? "Opening checkout" : label}
    </Button>
  );

  // "Make another book" with its price line (HANDOFF, "Make another book"): never shipping. At 10+ copies
  // the line names the 55% (decided 2026-10-26, "Show savings when prices drop").
  const anotherPriceLine = isBulkDiscounted(totalQuantity)
    ? "55% off every copy at 10+"
    : totalQuantity > 0
      ? `$${ADDITIONAL_BOOK_PRICE} each after your first book`
      : `Printed books from $${BASE_BOOK_PRICE}`;
  // Dashed button, centred with no icon, on every device and with any number of books (decided 2026-10-23).
  // A phone with one book used to get a link under Checkout instead.
  const addAnother = (
    <button
      type="button"
      onClick={handleAddBook}
      disabled={isProcessingOrder}
      className={`flex min-h-[60px] w-full items-center justify-center gap-2.5 rounded-[14px] border-2 border-dashed border-[#C9BCB0] px-4 py-2 text-[15px] font-semibold text-md-pink-dark hover:border-md-pink-dark disabled:opacity-50 md:min-h-[68px] md:rounded-2xl md:text-base`}
    >
      <span className="flex flex-col items-center gap-px text-center">
        <span>Make another book</span>
        <span className="text-[13px] font-medium text-md-muted md:text-sm">{anotherPriceLine}</span>
      </span>
    </button>
  );

  // Desktop with several books (Desk7Cart): the PDF offer sits under the summary, at the column's width.
  const summaryAside = cartItems.length ? (
    <div className="flex flex-col gap-4 lg:mt-[70px]">
      <aside className="flex flex-col gap-3.5 rounded-[20px] border border-md-pink-light-1 bg-white p-7">
        <h2 className="m-0 mb-1 font-sans text-lg font-bold text-md-dark">Order summary</h2>
        {totals}
        <div className="mt-1.5">{checkoutButton("Checkout")}</div>
        {/* TODO(design): the shipping time line ("Printed and shipped in [SHIPPING TIME]") waits on a value (HANDOFF open question). */}
      </aside>
      {!single && printedBooks.length ? offerCard({ column: true, idSuffix: "-side" }) : null}
    </div>
  ) : null;

  const bottomBar = cartItems.length ? (
    <BottomBar
      primary={
        <div className="flex items-center gap-4">
          <div className="hidden flex-col md:flex">
            <span className="text-sm text-md-muted">Subtotal</span>
            <span className="text-[22px] font-bold leading-tight text-md-dark">{money(subtotal)}</span>
          </div>
          <div className="min-w-0 flex-1">{checkoutButton(<><span className="md:hidden">Checkout · {money(subtotal)}</span><span className="hidden md:inline">Checkout</span></>)}</div>
        </div>
      }
    >
      {!single ? (
        <div className="flex justify-between text-[17px] font-bold text-md-dark md:hidden">
          <span>Subtotal</span>
          <span>{money(subtotal)}</span>
        </div>
      ) : null}
    </BottomBar>
  ) : null;

  return (
    <WizardShell plain aside={summaryAside} bottomBar={bottomBar}>
      <div className="flex flex-col gap-3.5">
        <Button variant="text" to={single ? undefined : "/dashboard"} onClick={single ? () => editBook(uniqueBooks[0].bookId) : undefined} icon={<ChevronLeft className="h-[18px] w-[18px]" aria-hidden="true" />} className="hidden self-start !px-0 lg:inline-flex">
          {single ? "Back to preview" : "Back to My Books"}
        </Button>
        <div className="flex items-baseline justify-between gap-3">
          <h1 className="m-0 font-heading text-[28px] font-normal leading-[1.1] text-md-dark md:text-[40px] lg:text-[44px]">Review your order</h1>
          {!single && cartItems.length ? <span className="whitespace-nowrap text-sm text-md-muted">{countText}</span> : null}
        </div>

        {cartItems.length === 0 ? (
          <>
            <EmptyState icon={<ShoppingBag className="h-8 w-8" aria-hidden="true" />} title="Your order is empty" action={<Button onClick={handleAddBook}>Start a new book</Button>}>
              Finish a book and add it to your order, or start a new one below.
            </EmptyState>
            {addAnother}
          </>
        ) : (
          <>
            {uniqueBooks.map((item) => {
              const book = getBook(item.bookId);
              const qty = getItemQuantity(item);
              const title = getCartItemDisplayTitle(item);
              if (isPdfItem(item)) {
                // A PDF-only book (OrderFormats, OrderPdfOnly): $9.95, no copies, no add-on; the printed book as an upgrade.
                const pdfCover = coverOf(book, item);
                const printHint = `${money((printedBooks.length ? ADDITIONAL_BOOK_PRICE : BASE_BOOK_PRICE) + PDF_ADDON_PRICE).replace(".00", "")} with your PDF, plus shipping`;
                return (
                  <div key={item.bookId} className="flex flex-col gap-3 rounded-[14px] border border-md-pink-light-1 bg-white p-3.5 md:p-4 lg:gap-4 lg:rounded-[20px] lg:px-7 lg:py-6">
                    <div className="flex gap-3 lg:gap-6">
                      <div aria-hidden="true" className="relative w-[54px] flex-shrink-0 self-start overflow-hidden rounded-[5px] md:w-[72px] lg:w-[84px] lg:rounded-lg">
                        <CoverPreview imageUrl={pdfCover} title={book?.title || title} />
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <div className="flex justify-between gap-2">
                          <span className="truncate text-[15px] font-bold text-md-dark md:text-base lg:text-[19px]">{title}</span>
                          <span className="text-[15px] font-bold text-md-dark md:hidden">{money(PDF_DOWNLOAD_PRICE)}</span>
                        </div>
                        <span className="text-[13px] text-md-muted md:text-sm lg:text-[15px]">Printable PDF · cover + 20 pages</span>
                        <StatusLabel status="pdf" className="mt-1 self-start" />
                        <div className="mt-1 hidden gap-4 md:flex">
                          <Button variant="text" onClick={() => editBook(item.bookId)} className="!min-h-9 !px-0 !text-sm lg:!text-[15px]">Edit book</Button>
                          <Button variant="text" onClick={() => removeWithUndo(item)} className="!min-h-9 !px-0 !text-sm !text-md-muted lg:!text-[15px]">Remove</Button>
                        </div>
                      </div>
                      <div className="hidden flex-col items-end gap-2 md:flex">
                        <span className="text-[19px] font-bold text-md-dark">{money(PDF_DOWNLOAD_PRICE)}</span>
                        <span className="text-[13px] text-md-muted">Emailed right away. No shipping.</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2 md:hidden">
                      <span className="text-sm text-[#4A4446]">Emailed right away. No shipping.</span>
                      <div className="flex gap-1">
                        <Button variant="text" onClick={() => editBook(item.bookId)} className="!text-sm">Edit</Button>
                        <Button variant="text" onClick={() => removeWithUndo(item)} className="!text-sm !text-md-muted" aria-label={`Remove ${title} from order`}>Remove</Button>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => toPrinted(item.bookId)}
                      disabled={isProcessingOrder || !!formatBusy}
                      className="flex min-h-12 items-center gap-3 rounded-[10px] border border-[#E6DDD3] bg-[#FBF8F3] px-3 py-1.5 text-left text-md-dark disabled:opacity-60 md:min-h-14 md:gap-3.5 md:px-4 md:py-2"
                    >
                      <Printer className="h-5 w-5 flex-shrink-0 text-md-pink-dark" aria-hidden="true" />
                      <span className="flex flex-1 flex-col gap-px">
                        <span className="text-[15px] font-semibold">{formatBusy === item.bookId ? "Adding the printed book" : "Add the printed book"}</span>
                        <span className="text-[13px] text-md-muted md:text-sm">{printHint}</span>
                      </span>
                      <ChevronRight className="h-5 w-5 flex-shrink-0 text-md-pink-dark" aria-hidden="true" />
                    </button>
                  </div>
                );
              }
              const pricing = pricingOf(item.bookId);
              const lineTotal = pricing?.price ?? 0;
              const discounted = !!pricing?.discounted;
              // Full price struck through beside the real one, on discounted books only.
              const priceTag = (strikeClass, priceClass) => (
                <span className="flex flex-shrink-0 items-baseline gap-1.5 md:gap-2">
                  {discounted ? (
                    <s className={`text-md-muted ${strikeClass}`}>
                      <span className="sr-only">Was </span>
                      {money(pricing.list)}
                    </s>
                  ) : null}
                  <span className={`font-bold text-md-dark ${priceClass}`}>{money(lineTotal)}</span>
                </span>
              );
              // Under "Copies" on a single book: the grey rule, or the green reason once it saves money.
              const copiesLine = (
                <span className={`text-[13px] ${discounted ? "font-semibold text-green-800" : "text-md-muted"}`}>
                  {discounted ? pricing.reason : `Extra copies $${ADDITIONAL_BOOK_PRICE} each`}
                </span>
              );
              const cover = coverOf(book, item);
              return (
                <div key={item.bookId} className="flex flex-col gap-3 rounded-[14px] border border-md-pink-light-1 bg-white p-3.5 md:p-4 lg:gap-4 lg:rounded-[20px] lg:px-7 lg:py-6">
                  <div className="flex gap-3 lg:gap-6">
                    <div aria-hidden="true" className="relative w-[54px] flex-shrink-0 self-start overflow-hidden rounded-[5px] md:w-[72px] lg:w-[84px] lg:rounded-lg">
                      <CoverPreview imageUrl={cover} title={book?.title || title} />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <div className="flex justify-between gap-2">
                        <span className={`text-[15px] font-bold text-md-dark md:text-base lg:text-[19px] ${single ? "" : "truncate"}`}>{single ? "Personalized coloring book" : title}</span>
                        <span className="md:hidden">{priceTag(single ? "text-sm" : "text-[13px]", single ? "text-base" : "text-[15px]")}</span>
                      </div>
                      {single ? <span className="text-sm text-md-muted lg:text-[15px]">{title}</span> : null}
                      <span className="text-[13px] text-md-muted md:text-sm lg:text-[15px]">
                        {/* Every printed book has 20 interior pages (blanks fill gaps); book.pageCount counts the cover and never drops, so it isn't shown. */}
                        {single ? "" : "Coloring book · "}Cover + 20 pages
                      </span>
                      {!single && discounted ? (
                        <span className="text-[13px] font-semibold text-green-800 md:text-sm">{pricing.reason}</span>
                      ) : null}
                      <div className={`mt-1 gap-4 ${single ? "flex" : "hidden md:flex"}`}>
                        <Button variant="text" onClick={() => editBook(item.bookId)} className="!min-h-9 !px-0 !text-sm lg:!text-[15px]">Edit book</Button>
                        <Button variant="text" onClick={() => removeWithUndo(item)} className={`!min-h-9 !px-0 !text-sm !text-md-muted lg:!text-[15px] ${single ? "hidden md:inline-flex" : ""}`}>Remove</Button>
                      </div>
                    </div>
                    <div className="hidden flex-col items-end gap-2 md:flex">
                      {priceTag("text-[15px]", "text-[19px]")}
                      <Stepper item={item} qty={qty} />
                      {single ? copiesLine : null}
                    </div>
                  </div>

                  <div className="flex items-center justify-between md:hidden">
                    {single ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[15px] font-semibold text-md-dark">Copies</span>
                        {copiesLine}
                      </div>
                    ) : null}
                    <Stepper item={item} qty={qty} />
                    {!single ? (
                      <div className="flex gap-1">
                        <Button variant="text" onClick={() => editBook(item.bookId)} className="!text-sm">Edit</Button>
                        <Button variant="text" onClick={() => removeWithUndo(item)} className="!text-sm !text-md-muted" aria-label={`Remove ${title} from order`}>Remove</Button>
                      </div>
                    ) : null}
                  </div>

                  {single ? (
                    offerCard({ inBook: true, className: "hidden md:flex" })
                  ) : (
                    <PdfRow
                      on={hasPdf(item.bookId)}
                      onToggle={() => setPdfIncluded([item.bookId], !hasPdf(item.bookId))}
                      title={title}
                      addOnPrice={PDF_ADDON_PRICE}
                      disabled={isProcessingOrder}
                    />
                  )}
                  {/* A book that started as a PDF here can go back (books that started printed switch from My Books only). */}
                  {item.startedAsPdf ? (
                    <Button
                      variant="text"
                      onClick={() => toPdfOnly(item.bookId)}
                      disabled={isProcessingOrder || !!formatBusy}
                      className="!min-h-10 self-start !px-1 !text-sm"
                    >
                      Switch back to PDF only · {money(PDF_DOWNLOAD_PRICE)}
                    </Button>
                  ) : null}
                </div>
              );
            })}

            {single ? offerCard({ idSuffix: "-phone", className: "flex md:hidden" }) : printedBooks.length ? offerCard({ className: "flex lg:hidden" }) : null}

            {addAnother}

            <div className="lg:hidden">{totals}</div>
            <div className="hidden items-center gap-2.5 rounded-xl border border-md-pink-light-1 bg-white px-3.5 py-3 text-sm text-[#4A4446]">
              <Truck className="h-5 w-5 flex-shrink-0 text-md-pink-dark" aria-hidden="true" />
              Printed and shipped in a few days
            </div>
          </>
        )}
      </div>
    </WizardShell>
  );
}

export default ShopifyCart;